import { call, all, getContext } from "typed-redux-saga";
import { getGlobalConfigs } from "@/index";
import { CMSClientType } from "@/cms-api";
import { FileInfoForSelectedJob } from "@centralwebteam/narwhal";
import { JobPresentation } from "@/presentation/Job";
import {
  createMeasurementCharacteristicPresentation,
  MeasurementCharacteristicPresentation,
} from "@/presentation/MeasurementCharacteristic";

export function* fetchJobMoreDetails({ jobId }: { jobId: string }) {
  try {
    const client = yield* getContext<CMSClientType>("client");
    const events = yield* call(
      (jobId) => client.events.byId<any>(jobId).promise,
      jobId
    );
    return events[0] as any | undefined;
  } catch (error) {
    console.error(error);
  }
}

export function* fetchFiles({ jobId }: { jobId: string }) {
  try {
    const client: CMSClientType = yield getContext("client");
    const fileDataForSelectedJob: FileInfoForSelectedJob[] = yield* call(
      (jobId) => client.files.byJobId(jobId).promise,
      jobId
    );
    return fileDataForSelectedJob;
  } catch (error) {
    console.error(error);
  }
}

export function* fetchJobMetaData({
  machineId,
  startDate,
  endDate,
}: {
  machineId: string;
  startDate: string;
  endDate: string;
}) {
  try {
    const client = yield* getContext<CMSClientType>("client");
    const JobMetadata: any = yield* call(
      (str) => client.events.query(str).promise,
      `$filter=machineId eq ${machineId} and type eq JobProperty and subType eq Metadata and created ge ${startDate} and created le ${endDate}`
    );
    const metadata = JobMetadata?.filter(
      (p: any) => p.displayHints?.userVisible
    ).map((item: any) => {
      return { [item.name]: item.value };
    });
    return Object.assign({}, ...metadata);
  } catch (error) {
    console.error(error);
  }
}

export function* fetchAllJobMetaData({
  startDate,
  endDate,
  machineId,
  take,
}: {
  startDate: string;
  endDate: string;
  machineId?: string;
  take?: number;
}) {
  try {
    const client = yield* getContext<CMSClientType>("client");
    const filterMachineId = machineId ? `and machineId eq ${machineId}` : "";
    const JobMetadata: any = yield* call(
      (str) => client.events.query(str).promise,
      `$filter=type eq JobProperty and subType eq Metadata and created ge ${startDate} and created le ${endDate} ${filterMachineId}${
        take ? `&$top=${take}` : ""
      }`
    );
    const metadata = JobMetadata.filter((p: any) => p.displayHints.userVisible);

    return metadata;
  } catch (error) {
    console.error(error);
  }
}

/** Date ranges of the latest `maxJobs` same-named jobs, sorted by start time ascending. */
export function getRecentSameNameJobRanges(
  jobs: JobPresentation[],
  jobName: string,
  timeRangeEnd: string,
  maxJobs: number = Infinity
) {
  const sorted = jobs
    .filter((j) => j.name === jobName)
    .sort((a, b) => (a.start < b.start ? -1 : 1));
  return sorted
    .slice(Math.max(0, sorted.length - maxJobs))
    .map((j) => ({ start: j.start, end: j.end ?? timeRangeEnd }));
}

export function* fetchMeasurementsForSelectedJob({
  from,
  to,
  machineId,
  ranges,
}: {
  from: string;
  to: string;
  machineId: string;
  ranges?: { start: string; end: string }[];
}) {
  const client = yield* getContext<CMSClientType>("client");
  const config = yield* call(() => getGlobalConfigs());
  const allRanges =
    ranges && ranges.length > 0 ? ranges : [{ start: from, end: to }];
  const CHUNK_SIZE = 17;
  const chunks: (typeof allRanges)[] = [];
  for (let i = 0; i < allRanges.length; i += CHUNK_SIZE) {
    chunks.push(allRanges.slice(i, i + CHUNK_SIZE));
  }
  try {
    const results = yield* all(
      chunks.map((chunk) => {
        const rangesFilter = chunk
          .map((r) => `created ge ${r.start} and created le ${r.end}`)
          .join(" or ");
        const filter =
          `$filter=machineId eq ${machineId} and (type eq MeasurementCharacteristic and (${rangesFilter}))` +
          `&$top=${config.maxTake}`;
        return call((q) => client.events.query(q).promise, filter);
      })
    );
    return results.flat().map(createMeasurementCharacteristicPresentation);
  } catch (error) {
    console.warn(error);
    return [] as MeasurementCharacteristicPresentation[];
  }
}
